import type { ConversationMessage, PlaceholderAnalysis } from '@/features/AI-documents/types/docGen.types';
import type {
  ChatFlow,
  ChatFlowContext,
  ChatFlowEventResult,
} from '@/features/startTask/preset_task/shared/preset/chatflows/types';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import { parseClauseNo, clauseBasisOf } from '../components/otc/otcFields';

/**
 * MY — Transfer of Shares chatflow — M32201.
 *
 * 3개 문서 패키지 — Variable Map: `Transfer_of_Shares_Preset_Draft.md`.
 *   1. Directors' Circular Resolution (Transfer) — 이사 전원(당사자 이사 이해관계 배제)
 *   2. Section 105 (Transfer)                    — 양도인 & 양수인 (+증인 4명)
 *   3. Waiver of Preemption Rights (Transfer)    — 매도자 제외 기존 주주 전원 (조건부: 정관 선매권 조항)
 *
 * 흐름:
 *   input-title → select-entity → confirm (panel 에서 변수 입력 + Create Task)
 *   ── Person Lookup ── (Director / Member / Transferor / Transferee 공통)
 *   officer-role → officer-list → officer-detail
 *   ── Address chooser ──
 *   address-list
 *
 * SKILL.md `my-preset` 의 표준 7-step + handleEvent 8 핸들러 답습 (allotmentSharesFlow 패턴).
 */
export const transferSharesFlow: ChatFlow = {
  id: 'transfer-shares',
  taskCodeMatch: ['M32201', 'M32204'], // with SC / without SC
  categoryMatch: ['transfer of shares', 'transfer shares', 'share transfer', 'transfer(shares)'],

  getInitialMessages(preSet: PreSetSelection): ConversationMessage[] {
    const docCount = preSet.documents.length;
    return [
      {
        role: 'ai',
        content:
          `Welcome to the Malaysia **Transfer of Shares** preset.\n\n` +
          `Please complete the transfer details below. Enter the seller, buyer, number of shares, ` +
          `certificate number, consideration amount, transfer date, and other required information.\n\n` +
          `If multiple share transfers are involved, click **Add Transfer**. Each transfer will ` +
          `automatically generate one **Section 105 – Instrument of Transfer** document.\n\n` +
          `After reviewing all information, click **Create Task** to generate the required documents ` +
          `(${docCount} document(s) in this preset).\n\n` +
          `First, please enter a **Task Title**.`,
        timestamp: Date.now(),
        stepId: 1,
        source: 'system',
      },
    ];
  },

  steps: [
    {
      id: 'input-title',
      aiMessage: 'Please enter a Task Title.',
      widget: 'text-input',
      widgetConfig: {
        targetVarKey: '_taskTitle',
        targetEntityIndex: 0,
      },
      awaitEvent: true,
    },
    {
      id: 'select-entity',
      aiMessage: 'Search and select the **entity** whose shares are being transferred.',
      widget: 'entity-selector',
      widgetConfig: {
        targetVarKey: '_entity',
        targetEntityIndex: 0,
        analysis: {
          success: true,
          placeholder: '_entity',
          sourceResolver: 'ENTITY',
          officerRole: null,
          varKey: '_entity',
          varLabel: 'Entity',
          confidence: 1,
          message: 'Select Entity (Transfer of Shares).',
          relatedFields: [],
          options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
    {
      id: 'confirm',
      aiMessage:
        'Entity linked. The right panel will auto-fill company / directors / existing members. ' +
        'Please input transfer details, consideration, dates, and the document variables. Click **Create Task** when ready.',
      widget: null,
      nextStep: null,
    },
    // ── Person Lookup steps (Director / Member / Transferor / Transferee 공통) ──
    {
      id: 'officer-role',
      aiMessage: 'Please select a role for the person you want to look up.',
      widget: 'officer-role-buttons',
      awaitEvent: true,
    },
    {
      id: 'officer-list',
      aiMessage: 'Select a person from the list.',
      widget: 'officer-list',
      awaitEvent: true,
    },
    {
      id: 'officer-detail',
      aiMessage:
        'Person details loaded. The form has been pre-filled. You can also drag/click the fields below.',
      widget: 'officer-field-picker',
      awaitEvent: true,
    },
    // ── Address chooser ──
    {
      id: 'address-list',
      aiMessage: 'This person/entity has multiple addresses. Pick the one to use.',
      widget: 'address-options',
      awaitEvent: true,
    },
    // ── Clause / Article No. (정관 결의 근거) ──
    {
      id: 'select-clause',
      aiMessage: 'Select a **Clause / Article No.** for the resolution, or type the number yourself.',
      widget: 'clause-number',
      widgetConfig: {
        targetVarKey: 'constitution_clause_number',
        targetEntityIndex: 0,
        clauseOptions: [],
        clauseDefaultBasis: 'constitution',
        analysis: {
          success: true, placeholder: 'constitution_clause_number', sourceResolver: 'SELECT', officerRole: null,
          varKey: 'constitution_clause_number', varLabel: 'Clause / Article No.', confidence: 1,
          message: 'Select or enter Clause / Article No.', relatedFields: [], options: [],
        } as PlaceholderAnalysis,
      },
      awaitEvent: true,
    },
  ],

  handleEvent(eventName: string, payload: any, context: ChatFlowContext): ChatFlowEventResult {
    // 1) Task Title
    if (eventName === 'value-submit' && context.currentStepId === 'input-title') {
      const title = typeof payload === 'string' ? payload : payload?.value || payload;
      if (!title || !String(title).trim()) return {};
      return {
        messages: [
          { role: 'user', content: String(title), timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: { taskTitle: String(title).trim() },
        nextStepId: 'select-entity',
      };
    }

    // 2) Entity 선택
    if (eventName === 'entity-select' && context.currentStepId === 'select-entity') {
      const ent = payload || {};
      const entityCd = ent.entityCd || ent.icpCd || '';
      const entityName = ent.entityName || ent.companyName || 'Entity';
      if (!entityCd) return {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${entityName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        formUpdates: {
          entityCd,
          entityName,
          icpCd: entityCd,
          registrationNo: ent.registrationNo || '',
        },
        nextStepId: 'confirm',
      };
    }

    // 3a) 정관 Clause / Article No. — 전용 clause-number 위젯 (AI 챗봇에서 선택/입력)
    if (eventName === 'request-person-lookup' && (payload?.fieldGroup === 'constitution-clause' || payload?.key === 'constitution_clause_number')) {
      const ctx = payload || {};
      const options = Array.isArray(ctx.clauseOptions)
        ? ctx.clauseOptions.map((o: any) => String(o ?? '').trim()).filter(Boolean)
        : [];
      return {
        messages: [{ role: 'user', content: 'Looking up **Clause / Article No.**…', timestamp: Date.now(), stepId: 1 }],
        nextStepId: 'select-clause',
        nextStepWidgetData: {
          targetVarKey: 'constitution_clause_number',
          clauseOptions: options,
          clauseDefaultBasis: ctx.defaultBasis || 'constitution',
          defaultValue: ctx.defaultValue || '',
          analysis: {
            success: true, placeholder: 'constitution_clause_number', sourceResolver: 'SELECT', officerRole: null,
            varKey: 'constitution_clause_number', varLabel: 'Clause / Article No.', confidence: 1,
            message: 'Select or enter Clause / Article No.', relatedFields: [], options,
          } as PlaceholderAnalysis,
        },
      };
    }

    // 3) Panel 의 AI 아이콘 클릭 → 사람 lookup 진입
    if (eventName === 'request-person-lookup') {
      const ctx = payload || {};
      const entityCd = context.entityCd || context.formData.entityCd || '';
      if (!entityCd) {
        return {
          messages: [
            { role: 'ai', content: 'Please select an entity first.', timestamp: Date.now(), stepId: 1, source: 'system' },
          ],
        };
      }
      if (ctx.defaultRole) {
        return {
          messages: [
            { role: 'user', content: `Looking up **${ctx.fieldGroup}** (role: ${ctx.defaultRole})...`, timestamp: Date.now(), stepId: 1 },
          ],
          nextStepId: 'officer-list',
          nextStepWidgetData: { entityCd, selectedRole: ctx.defaultRole },
        };
      }
      return {
        messages: [
          { role: 'user', content: `Looking up **${ctx.fieldGroup}**...`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-role',
      };
    }

    // 4) Officer Role 선택
    if (eventName === 'officer-role-select' && context.currentStepId === 'officer-role') {
      const role = payload;
      const entityCd = context.entityCd || context.formData.entityCd || '';
      return {
        messages: [
          { role: 'user', content: `Role: **${role}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-list',
        nextStepWidgetData: { entityCd, selectedRole: role },
      };
    }

    // 5) Officer 선택
    if (eventName === 'officer-select' && context.currentStepId === 'officer-list') {
      const officer = payload || {};
      const officerName = officer.name || officer.contactName || 'Selected person';
      return {
        messages: [
          { role: 'user', content: `Selected person: **${officerName}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'officer-detail',
      };
    }

    // 6) officer-list 뒤로가기
    if (eventName === 'officer-list-back' && context.currentStepId === 'officer-list') {
      return { nextStepId: 'officer-role' };
    }

    // 7) Address lookup 요청
    if (eventName === 'request-address-lookup') {
      const ctx = payload || {};
      const opts = Array.isArray(ctx.addresses) ? ctx.addresses : [];
      const subject = ctx.subjectLabel || 'this person';
      return {
        messages: [
          { role: 'user', content: `Choose an address for **${subject}**.`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'address-list',
        nextStepWidgetData: { addressOptions: opts as any },
      };
    }

    // 8) address option 선택
    if (eventName === 'address-option-select') {
      const opt = payload || {};
      return {
        messages: [
          { role: 'user', content: `Selected: **${opt.label || opt.type}**`, timestamp: Date.now(), stepId: 1 },
        ],
        nextStepId: 'confirm',
      };
    }

    // 9) 정관 Clause / Article No. 선택 확정 → constitution_clause_number + basis (studio 는 applied-clause 로 패널 반영)
    if (eventName === 'clause-number-select') {
      const pick = payload || {};
      const clauseNo = String(pick.clauseNumber || '').trim();
      const rawOption = String(pick.rawOption || '').trim();
      const detected = clauseBasisOf(rawOption);
      const basis = pick.governingBasis || (detected !== 'other' ? detected : null) || (clauseNo ? 'constitution' : null);
      const label = basis === 'third_schedule' ? 'Third Schedule'
        : clauseNo ? `${basis === 'articles' ? 'Article' : 'Clause'} ${parseClauseNo(clauseNo) || clauseNo}` : 'Clause';
      return {
        messages: [{ role: 'user', content: `Governing document: **${label}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          constitution_clause_number: basis === 'third_schedule' ? '' : (parseClauseNo(clauseNo) || clauseNo),
          _clauseSyncAt: Date.now(),
          _clauseRawOption: rawOption,
        },
        nextStepId: 'confirm',
      };
    }
    if (eventName === 'value-submit' && context.currentStepId === 'select-clause') {
      const raw = typeof payload === 'string' ? payload : payload?.value || payload;
      const rawStr = String(raw || '').trim();
      if (!rawStr) return {};
      const detected = clauseBasisOf(rawStr);
      return {
        messages: [{ role: 'user', content: `Clause / Article No.: **${rawStr}**`, timestamp: Date.now(), stepId: 1 }],
        formUpdates: {
          constitution_clause_number: detected === 'third_schedule' ? '' : (parseClauseNo(rawStr) || rawStr),
          _clauseSyncAt: Date.now(), _clauseRawOption: rawStr,
        },
        nextStepId: 'confirm',
      };
    }

    return {};
  },
};
